import { useMemo, useCallback, useState } from "react";

// Constants for CSS classes
const CSS_CLASSES = {
  FIRM_NAME_CELL: "firm-name-cell",
  OFFICE_LOCATION_CELL: "office-location-cell",
} as const;
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import type {
  GridColDef,
  GridRowSelectionModel,
  GridRowParams,
} from "@mui/x-data-grid";
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from "@mui/icons-material";
import type { FirmResponse } from "@taxassist/api-client";
import { NotificationSnackbar } from "../common/NotificationSnackbar";

interface FirmTableProps {
  firms: FirmResponse[];
  isLoading: boolean;
  onEditFirm?: (firm: FirmResponse) => void;
  onViewFirm: (firm: FirmResponse) => void;
  onToggleFirmStatus?: (firm: FirmResponse) => void;
  selectedFirms: Set<string>;
  onSelectionChange: (selection: Set<string>) => void;
  canModifyFirms?: boolean;
}

export function FirmTable({
  firms = [],
  isLoading = false,
  onEditFirm,
  onViewFirm,
  onToggleFirmStatus,
  selectedFirms = new Set(),
  onSelectionChange,
  canModifyFirms = true,
}: FirmTableProps) {
  const theme = useTheme();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Transform firms data for DataGrid - memoize the transformation function
  const transformFirmToRow = useCallback(
    (firm: FirmResponse) => ({
      id: firm.firmId,
      name: firm.name,


      address: firm.address || "N/A",
      isActive: firm.isActive,
      createdAt: firm.createdAt,
      updatedAt: firm.updatedAt,
      createdBy: firm.createdByEmail || firm.createdBy,
      officeLocationId: firm.surePrepConfig?.officeLocationId || "N/A",
      firmData: firm, // Store full firm data for actions
    }),
    [],
  );

  const rows = useMemo(() => {
    if (!Array.isArray(firms)) {
      return [];
    }
    return firms.map(transformFirmToRow);
  }, [firms, transformFirmToRow]);

  // Handle row selection
  const handleSelectionModelChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      if (onSelectionChange && Array.isArray(newSelection)) {
        const stringSelection = newSelection.filter(
          (id): id is string => typeof id === "string",
        );
        onSelectionChange(new Set(stringSelection));
      }
    },
    [onSelectionChange],
  );


  // Handle row click
  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      const firm = params.row.firmData as FirmResponse;
      onViewFirm(firm);
    },
    [onViewFirm],
  );

  // Format date for display
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  // Memoize action handlers to prevent recreation on every render
  const getActionsForRow = useCallback(
    (params: GridRowParams) => {
      const firm = params.row.firmData as FirmResponse;
      const actions = [
        <GridActionsCellItem
          key="view"
          icon={<VisibilityIcon />}
          label="View"
          onClick={() => onViewFirm(firm)}
          showInMenu
        />,
      ];

      // Only add edit/toggle actions if user can modify firms
      if (canModifyFirms && onEditFirm) {
        actions.unshift(
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => onEditFirm(firm)}
            showInMenu
          />,
        );
      }

      if (canModifyFirms && onToggleFirmStatus) {
        actions.push(
          <GridActionsCellItem
            key="toggle"
            icon={firm.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
            label={firm.isActive ? "Deactivate" : "Activate"}
            onClick={() => onToggleFirmStatus(firm)}
            showInMenu
          />,
        );
      }

      return actions;
    },
    [onEditFirm, onViewFirm, onToggleFirmStatus, canModifyFirms],
  );

  // Column definitions
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Firm Name",
        flex: 1.2,
        minWidth: 180,
        headerAlign: "left",
        align: "left",
        cellClassName: CSS_CLASSES.FIRM_NAME_CELL,
      },

      {
        field: "isActive",
        headerName: "Status",
        flex: 0.6,
        minWidth: 100,
        headerAlign: "center",
        align: "center",
        type: "boolean",
        valueFormatter: (value) => (value ? "Active" : "Inactive"),
        cellClassName: (params) =>
          params.value ? "status-active" : "status-inactive",
      },
      {
        field: "officeLocationId",
        headerName: "SurePrep Office",
        flex: 0.8,
        minWidth: 120,
        headerAlign: "center",
        align: "center",
        cellClassName: CSS_CLASSES.OFFICE_LOCATION_CELL,
      },
      {
        field: "createdAt",
        headerName: "Created",
        flex: 0.8,
        minWidth: 120,
        headerAlign: "left",
        align: "left",
        sortable: true,
        type: "dateTime",
        valueGetter: (value) => new Date(value),
        valueFormatter: (value) => formatDate(value.toISOString()),
      },
      {
        field: "createdBy",
        headerName: "Created By",
        flex: 1,
        minWidth: 150,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        flex: 0.8,
        minWidth: 120,
        headerAlign: "center",
        getActions: getActionsForRow,
      },
    ],
    [getActionsForRow, formatDate],
  );

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        checkboxSelection
        onRowSelectionModelChange={handleSelectionModelChange}
        onRowClick={handleRowClick}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 },
          },
        }}
        disableColumnResize
        disableColumnMenu
        hideFooterSelectedRowCount
        autoHeight
        rowHeight={52}
        disableVirtualization={false}
        sx={{
          border: "none",
          "& .MuiDataGrid-main": {
            borderRadius: 0,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[50],
            borderBottom: `2px solid ${theme.palette.divider}`,
            "& .MuiDataGrid-columnHeader": {
              fontWeight: 600,
              fontSize: "0.875rem",
            },
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
          },
          "& .MuiDataGrid-row": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            cursor: "pointer",
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
            "&.Mui-selected": {
              backgroundColor: theme.palette.action.selected,
              "&:hover": {
                backgroundColor: theme.palette.action.focus,
              },
            },
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: `2px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
          },
          "& .MuiCheckbox-root": {
            color: theme.palette.primary.main,
          },
          // Custom cell styles
          "& .firm-name-cell": {
            fontWeight: 500,
          },
          "& .status-active": {
            color: theme.palette.success.main,
            fontWeight: 600,
          },
          "& .status-inactive": {
            color: theme.palette.grey[600],
            fontWeight: 600,
          },
          "& .office-location-cell": {
            fontWeight: 500,
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  fontSize: "3rem",
                  opacity: 0.3,
                }}
              >
                🏢
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Box sx={{ fontWeight: 500, mb: 1 }}>No firms found</Box>
                <Box sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                  Create your first firm to get started
                </Box>
              </Box>
            </Box>
          ),
          loadingOverlay: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">
                Loading firms...
              </Typography>
            </Box>
          ),
        }}
      />

      {/* Success Snackbar */}
      <NotificationSnackbar
        message={successMessage}
        onClose={() => setSuccessMessage(null)}
        severity="success"
      />

      {/* Error Snackbar */}
      <NotificationSnackbar
        message={errorMessage}
        onClose={() => setErrorMessage(null)}
        severity="error"
      />
    </Box>
  );
}
